// eslint-disable-next-line simple-import-sort/imports
import {ReactElement, useCallback, useEffect, useState} from "react";
import {styled} from "styled-components";

import {getContrat, reouvrirContrat} from "@/Api/contrat";
import IconContact from "@/assets/iconContact.svg?react";
import IconSee from "@/assets/iconSee.svg?react";

import {Button} from "@/components/Button/Button";
import {ErrorBoundary} from "@/components/ErrorBoundary/ErrorBoundary";
import {FournisseurHeader} from "@/components/Fournisseur/FournisseurHeader/FournisseurHeader";

import {LoadingOrError} from "@/components/LoadingOrError/LoadingOrError";
import {Notification} from "@/components/notification/Notification";

import {AppContext} from "@/context/AppContext";
import {HookFormContext, HookFormContextType} from "@/context/HookFormContext";

import {breakpoints, quicksandSemiBold} from "@/GlobalStyle";
import {useGetContext} from "@/hooks/useGetContext/useGetContext";
import {useLoadingState} from "@/hooks/useLoadingState/useLoadingState";
import {
  FournisseurInfo,
  Notification as NotificationType,
} from "@/ts/fournisseur";
import {CampagneFournisseur, FournisseurBonus} from "@/ts/kpi";
import {AppContext as AppContextType} from "@/ts/appContext";
import {Contrat} from "@/ts/contrat";
import {AdherentBase} from "@/ts/adherent";
import {
  UserParamContext,
  UserParamContextType,
} from "@/context/UserParamContext";
import {AdherentSelecteur} from "@/components/Adherent/AdherentSelecteur";

import {BlocSuiviContrat} from "@/components/Contrat/BlocSuiviContrat";
import {getFournisseurTableauBord as fetchFournisseur} from "@/Api/fournisseur";
import {FournisseurUserTableauBord as FournisseurUserTableauBordType} from "@/ts/fournisseur";

import IconUnlock from "@/assets/iconUnlock.svg?react";

const ContentSelectedAdherent = styled.div`
  margin: 15px auto 0;
  width: 50%;
  @media (max-width: ${breakpoints.md}) {
    width: 90%;
  }
`;

const TitleNotificaton = styled.p`
  font-size: 1.25rem;
  ${quicksandSemiBold};
  color: ${({theme}) => theme.colors.lightPrimary};
`;

const ButtonCloturerContrat = styled(Button)`
  margin: 15px 0;
  text-align: center;
  justify-content: center;
  border: 2px solid white;
  background-color: ${({theme}) => theme.colors.mediumSecondary};
  outline: 2px solid ${({theme}) => theme.colors.mediumSecondary};
  & span {
    font-size: 1rem;
  }
`;

export function FournisseurUserTableauBord({
  fournisseur,
  bonus,
  campagne,
  notifications: notificationsInitial,
  adherents,
}: {
  fournisseur: FournisseurInfo;
  campagne: CampagneFournisseur;
  bonus: FournisseurBonus[];
  notifications: NotificationType[];
  adherents: AdherentBase[];
}): ReactElement {
  const [notifications, setNotifications] =
    useState<NotificationType[]>(notificationsInitial);

  const {API = undefined} = useGetContext<AppContextType>(
    AppContext,
    "AppContext"
  );
  const {currentCampagne} = useGetContext<UserParamContextType>(
    UserParamContext,
    "UserParamContext"
  );

  const refreshAllNotifications = useCallback(async () => {
    const reponse =
      await notificationLoadingState.executeWithLoading<FournisseurUserTableauBordType>(
        () => fetchFournisseur(API, currentCampagne.value)
      );
    const {notifications: notificationsReponse} = reponse;
    setNotifications(notificationsReponse);
  }, []);

  const {watch, setValue} = useGetContext<HookFormContextType>(
    HookFormContext,
    "HookFormContext"
  );
  const selectedAdherent = watch("selected_adherent");
  const [contrat, setContrat] = useState<Contrat | undefined>(undefined);
  const notificationLoadingState = useLoadingState();
  const loadingState = useLoadingState();
  const getContratFournisseurAdherent = useCallback(
    async (valueSelectedAdherent: number): Promise<void> => {
      const reponse = await loadingState.executeWithLoading<Contrat>(() =>
        getContrat(API, valueSelectedAdherent)
      ); // TODO: Récupérer le contrat de l'adhérent
      setContrat(reponse);
    },
    [loadingState, API]
  );

  useEffect(() => {
    if (selectedAdherent?.value && currentCampagne?.value !== "") {
      setValue("selected_adherent", undefined);
    }
  }, [currentCampagne?.value]);

  return (
    <>
      <FournisseurHeader
        fournisseurInfo={fournisseur}
        bonus={bonus}></FournisseurHeader>
      {campagne ? (
        <>
          <ContentSelectedAdherent id="adherent-select">
            <AdherentSelecteur
              name="selected_adherent"
              adherents={adherents}
              handleSelect={getContratFournisseurAdherent}
              handleTrash={() => setContrat(undefined)}
            />
          </ContentSelectedAdherent>
          {selectedAdherent ? (
            <LoadingOrError
              loadingState={loadingState}
              errorImage="/path/to/error-image.png"
              buttonToRefresh={true}
              onRetry={async () => {
                if (selectedAdherent?.value) {
                  await getContratFournisseurAdherent(selectedAdherent.value);
                }
              }}
              data={contrat}
              dataValidator={data =>
                data &&
                data?.campagne &&
                data?.campagne?.bonus &&
                data?.campagne?.kpis
              }>
              {contrat && (
                <ErrorBoundary
                  fallbackText="Erreur lors de la récupération du contrat"
                  buttonToRefresh={true}>
                  {new Date(contrat.campagne?.fin) < new Date() &&
                  contrat?.statut === "cloture" ? (
                    <ButtonCloturerContrat
                      label={"Réouvrir le contrat"}
                      variant="secondary"
                      icon={IconUnlock}
                      onClick={async () => {
                        await reouvrirContrat(API, contrat?.contrat_id);
                        await getContratFournisseurAdherent(
                          selectedAdherent.value
                        );
                      }}
                    />
                  ) : null}
                  <BlocSuiviContrat
                    contrat={contrat}
                    refreshAllNotifications={refreshAllNotifications}
                  />
                </ErrorBoundary>
              )}
            </LoadingOrError>
          ) : null}
          {notifications?.length || 0 > 0 ? (
            <LoadingOrError
              loadingState={notificationLoadingState}
              errorImage="/path/to/error-image.png"
              buttonToRefresh={true}
              onRetry={async () => {
                await refreshAllNotifications();
              }}
              data={notifications}
              dataValidator={data => data}>
              <>
                <TitleNotificaton>Notification adhérents</TitleNotificaton>
                {notifications?.map((notification, index) => {
                  const isContactNotification =
                    notification.type === "Demande de contact";
                  return (
                    <Notification
                      key={index}
                      notification={notification}
                      refreshAllNotifications={refreshAllNotifications}>
                      <Button
                        label={
                          isContactNotification ? "Contacter" : "Consulter"
                        }
                        icon={isContactNotification ? IconContact : IconSee}
                        onClick={() => {
                          if (isContactNotification) {
                          } else {
                            // Scroll vers la liste déroulante adhérent
                            document
                              .getElementById("adherent-select")
                              ?.scrollIntoView({
                                behavior: "smooth",
                                block: "center",
                              });
                            setValue("selected_adherent", {
                              libelle: `${notification.adherent.contremarque} - ${notification.adherent.nom}`,
                              value: notification.adherent.contrat_id,
                            });
                          }
                        }}
                      />
                    </Notification>
                  );
                })}
              </>
            </LoadingOrError>
          ) : (
            <p>Aucune notification en cours sur cette campagne</p>
          )}
        </>
      ) : (
        <p>
          Vous ne participez à aucune campagne, veuillez contacter votre contact
          Cofaq pour participer à une campagne
        </p>
      )}
    </>
  );
}
